import { useEffect } from "react";
import { Helmet } from "react-helmet";
import { LuLoaderCircle } from "react-icons/lu";
import { useIsUserSignedIn } from "../../core_stores/useAuthStore";
import { colors, typography } from "../../design/tokens";
import { DeviceType } from "../../model/rankings";
import {
  SideMenuBehavior,
  useSideMenuBehavior,
} from "../../view_stores/useSideMenuStore";
import SideMenuPanel from "../sideMenu/SideMenuPanel";
import { WelcomeAppBar } from "../welcome/components/WelcomeAppBar";
import { WelcomeFooter } from "../welcome/components/WelcomeFooter";
import { AppStoreRankingsDropdown } from "./components/AppStoreRankingsDropdown";
import { AppStoreRankingsGrid } from "./components/AppStoreRankingsGrid";
import { AppStoreRankingsSignInPrompt } from "./components/AppStoreRankingsSignInPrompt";
import { AppStoreRankingsStoreToggle } from "./components/AppStoreRankingsStoreToggle";
import { AppStoreRankingsSearch } from "./components/AppStoreRankingsSearch";
import { AppStoreRankingsSearchModal } from "./components/AppStoreRankingsSearchModal";
import { AppStoreRankingsSEOText } from "./components/AppStoreRankingsSEOText";
import { preloadImages, clearPreloadCache } from "../../utils/imagePreloader";
import { useAppStoreRankingsLogic } from "./hooks/useAppStoreRankingsLogic";

export function AppStoreRankingsPageDefault() {
  const isUserSignedIn = useIsUserSignedIn();
  const sideMenuBehavior = useSideMenuBehavior();

  // Extract common business logic into reusable hook
  const {
    filters,
    setFilters,
    rankings,
    isLoading,
    error,
    searchQuery,
    setSearchQuery,
    searchResults,
    isSearching,
    isSearchModalOpen,
    countryOptions,
    categoryOptions,
    deviceOptions,
    subcategoryOptions,
    searchPlaceholder,
    fetchRankings,
    handleStoreChange,
    handleCategoryChange,
    handleSearch,
    handleClearSearch,
    handleCloseSearchModal,
    isGamesCategory,
  } = useAppStoreRankingsLogic({
    onStoreChange: clearPreloadCache,
  });

  // Preload top 15 icons from each category when rankings load
  // This improves perceived performance on slow networks
  useEffect(() => {
    if (rankings) {
      const iconUrlsToPreload = [
        ...rankings.free.slice(0, 15).map((app) => app.icon),
        ...rankings.paid.slice(0, 15).map((app) => app.icon),
        ...rankings.grossing.slice(0, 15).map((app) => app.icon),
      ];

      // Preload in background - don't await, let UI render first
      preloadImages(iconUrlsToPreload).catch(() => {
        // Silently ignore errors - AppIcon component will handle retries
      });
    }
  }, [rankings]);

  return (
    <>
      <Helmet>
        <title>Discover Apps - iOS & Android | Darvin</title>
        <meta
          name="description"
          content="Browse real-time app rankings from iOS App Store and Google Play. See top free, paid, and grossing apps across categories and countries."
        />
      </Helmet>

      <div className="min-h-screen flex">
        {isUserSignedIn && <SideMenuPanel />}
        <div
          className={`${
            isUserSignedIn
              ? sideMenuBehavior === SideMenuBehavior.PINNED
                ? "pl-[320px]"
                : "pl-[56px]"
              : ""
          } flex-1 flex flex-col transition-[padding] duration-300 relative`}
        >
          {/* Header with gradient background */}
          <div className="flex flex-col relative bg-gradient-hero-dark">
            <div className="relative z-10">
              <WelcomeAppBar />
            </div>
            <div className="flex justify-center px-8 pt-8 pb-16 relative z-10">
              <div className="w-full max-w-[1300px] text-center">
                <h1
                  className={`text-[48px] ${typography.weight.bold} text-white mb-4`}
                >
                  Discover Apps
                </h1>
                <p className="text-xl text-white/80">
                  Search apps or browse top rankings across every category and country. Get inspired and build something similar.
                </p>
              </div>
            </div>
            {/* Fade transition overlay */}
            <div className="absolute bottom-0 left-0 right-0 h-32 pointer-events-none bg-gradient-to-b from-transparent via-dark-surface-primary/60 to-dark-surface-primary z-10" />
          </div>

          {/* Main content */}
          <div className={`flex-1 ${colors.surface.primary} px-8`}>
            <div className="w-full max-w-[1300px] mx-auto pt-8 pb-16">
              {/* Filters row */}
              <div className="flex items-end gap-6 mb-4">
                <AppStoreRankingsStoreToggle selected={filters.store} onChange={handleStoreChange} />
                <AppStoreRankingsDropdown
                  options={countryOptions}
                  value={filters.country}
                  onChange={(v) => setFilters(prev => ({ ...prev, country: v }))}
                  className="w-[180px]"
                />
                {filters.store === "ios" && (
                  <AppStoreRankingsDropdown
                    options={deviceOptions}
                    value={filters.device}
                    onChange={(v) => setFilters(prev => ({ ...prev, device: v as DeviceType }))}
                    className="w-[120px]"
                  />
                )}
                <AppStoreRankingsDropdown
                  options={categoryOptions}
                  value={filters.category}
                  onChange={handleCategoryChange}
                  className="w-[180px]"
                />
                {isGamesCategory(filters.category) && filters.store === "android" && (
                  <AppStoreRankingsDropdown
                    options={subcategoryOptions}
                    value={filters.subcategory}
                    onChange={(v) => setFilters(prev => ({ ...prev, subcategory: v }))}
                    className="w-[160px]"
                  />
                )}
                <div className="flex-1" />
                <div className="w-full max-w-[400px]">
                  <AppStoreRankingsSearch
                    value={searchQuery}
                    onChange={setSearchQuery}
                    onSearch={handleSearch}
                    onClear={handleClearSearch}
                    isSearching={isSearching}
                    placeholder={searchPlaceholder}
                  />
                </div>
              </div>

              {/* Content area */}
              {isLoading ? (
                <div className="flex items-center justify-center py-20">
                  <LuLoaderCircle
                    className={`animate-spin ${colors.text.secondary}`}
                    size={40}
                  />
                </div>
              ) : error ? (
                <div className="flex flex-col items-center justify-center py-20">
                  <p className={`${colors.text.secondary} mb-4`}>{error}</p>
                  <button
                    onClick={fetchRankings}
                    className="px-4 py-2 bg-primary-blue text-white text-sm font-semibold tracking-wide rounded-full hover:opacity-90 transition-opacity"
                  >
                    Try Again
                  </button>
                </div>
              ) : rankings ? (
                <>
                  <AppStoreRankingsGrid
                    rankings={rankings}
                    store={filters.store}
                    country={filters.country}
                  />
                  {!isUserSignedIn && <AppStoreRankingsSignInPrompt />}
                </>
              ) : null}
            </div>
          </div>

          <AppStoreRankingsSEOText />
          <WelcomeFooter />
        </div>
      </div>

      {/* Search Results Modal */}
      <AppStoreRankingsSearchModal
        isOpen={isSearchModalOpen}
        onClose={handleCloseSearchModal}
        searchQuery={searchQuery}
        results={searchResults}
        rankings={rankings}
        store={filters.store}
        country={filters.country}
        isSearching={isSearching}
      />
    </>
  );
}
